import { skipToken, useSuspenseQueries } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { trpc } from '@/main'
import { deriveProfiles, type ProfileRouteParams } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

type ReportInput = Exclude<Parameters<typeof trpc.reports.getFrontendReport.queryOptions>[0], typeof skipToken>

// Just these keys: spreading all of `search` put `q` in the report key and refetched on every list-search keystroke.
const REPORT_INPUT_KEYS = [
  'first',
  'second',
  'selected',
  'type',
  'orgType',
  'teamId',
  'evaluation',
  'members',
  'name',
  // The two report pages paint disjoint sections, so each is its own response rather than one filtered twice.
  'pageType',
] as const satisfies readonly (keyof ReportInput)[]

function reportInput(profiles: { first: unknown; second: unknown }) {
  if (!profiles.first && !profiles.second) return skipToken
  const input = {} as ReportInput
  for (const key of REPORT_INPUT_KEYS) {
    const value = (profiles as Record<string, unknown>)[key]
    if (value !== undefined) (input as Record<string, unknown>)[key] = value
  }
  return input
}

/**
 * The report panes that read a pane-specific query — the board and the distribution — matched against the
 * pathname the LOADER is running for. Their query is declared on the LAYOUT above them but must be warmed
 * only here: see `scripts/check-route-prefetch.md` ("Check 12"), which fails a layout that warms it on
 * every pane.
 */
export const PANES_WITH_OWN_QUERY = /\/(board|distribution)\/?$/

// Only /report ('own') and /market need this; every other route derives its profiles from path params or search.
export type ReportSurface = 'own' | 'market'

// Belongs on the route's context so the loader and the component share ONE deriveProfiles result.
export function reportQueryOptions(deps: ReportSearchDeps, params: ProfileRouteParams, surface?: ReportSurface) {
  const profiles = deriveProfiles(deps, params, surface === 'own', 'search', surface === 'market')
  const input = reportInput({ ...deps, ...profiles })
  return input === skipToken ? undefined : trpc.reports.getFrontendReport.queryOptions(input)
}

/** The options `reportQueryOptions` returns when a profile IS selected — what components consume. */
export type ReportQueryOptions = NonNullable<ReturnType<typeof reportQueryOptions>>

export type ReportSearchDeps = Pick<RootSearch, (typeof REPORT_INPUT_KEYS)[number]>

// Exactly REPORT_INPUT_KEYS, so unrelated search churn (dialog ids, `q` keystrokes) does not re-run these loaders.
export function reportSearchDeps(search: ReportSearchDeps): ReportSearchDeps {
  return {
    first: search.first,
    second: search.second,
    selected: search.selected,
    type: search.type,
    orgType: search.orgType,
    teamId: search.teamId,
    evaluation: search.evaluation,
    members: search.members,
    name: search.name,
    pageType: search.pageType,
  }
}

// useSuspenseQueries takes an ARRAY, so "no profile selected" is an empty array rather than a conditional hook call.
export function useReport(queryOptions: ReportQueryOptions | undefined) {
  const deferred = useDeferredValue(queryOptions)
  // Annotated: an inline `cond ? [x] : []` narrows to a tuple union that `useSuspenseQueries` can't read.
  const queries: ReportQueryOptions[] = deferred ? [deferred] : []
  const [result] = useSuspenseQueries({ queries })
  // The KEY, not identity: the route mints fresh options whenever search this report does not key on changes.
  const isTransitioning = JSON.stringify(deferred?.queryKey) !== JSON.stringify(queryOptions?.queryKey)
  return {
    report: result?.data,
    isFetching: (result?.isFetching ?? false) || isTransitioning,
  }
}
